import _ from 'lodash';
import * as jsonPatch from 'fast-json-patch';

import { COLLECTION_TYPES } from '@/core/collections/constants';

// TODO: type this — Vuex module context and player object types
/* eslint-disable @typescript-eslint/no-explicit-any */
type Ctx = any;
/* eslint-enable @typescript-eslint/no-explicit-any */

export const getters = {
    getPlayer: (_state: Ctx, _getters: Ctx, _rootState: Ctx, rootGetters: Ctx) => (id: string) => {
        return rootGetters['collections/getItem'](COLLECTION_TYPES.players, id);
    },
    getPlayers: (_state: Ctx, _getters: Ctx, _rootState: Ctx, rootGetters: Ctx) => {
        return rootGetters['collections/getCollection'](COLLECTION_TYPES.players);
    },
    gameMasters(_state: Ctx, getters: Ctx, rootState: Ctx) {
        const players = getters.getPlayers;
        const gameMasters = createGmGroup(players);
        if (!_.isEmpty(gameMasters)) {
            return gameMasters;
        } else {
            const ownerId = rootState.campaigns.activeCampaign.owner;
            return { [ownerId]: ownerId };
        }
    },
    nonGameMasters(_state: Ctx, getters: Ctx) {
        const players = getters.getPlayers;
        const nonGameMasters = createNonGmGroup(players, getters.gameMasters);
        return nonGameMasters;
    },
    isGameMaster: (_state: Ctx, getters: Ctx) => (userId: string) => {
        return userId in getters.gameMasters;
    },
};

export const actions = {
    async add({ dispatch }: Ctx, { player }: Ctx): Promise<unknown> {
        return await dispatch(
            'collections/add',
            { collection: COLLECTION_TYPES.players, item: player },
            { root: true },
        );
    },
    async update({ dispatch }: Ctx, { id, patch }: Ctx): Promise<unknown> {
        return await dispatch(
            'collections/update',
            { collection: COLLECTION_TYPES.players, id, patch },
            { root: true },
        );
    },
    async remove({ dispatch }: Ctx, playerId: string): Promise<unknown> {
        return await dispatch(
            'collections/remove',
            { collection: COLLECTION_TYPES.players, id: playerId },
            { root: true },
        );
    },
    async load({ dispatch }: Ctx): Promise<unknown> {
        return await dispatch('collections/load', { collection: COLLECTION_TYPES.players }, { root: true });
    },
    async setGameMasterStatus({ dispatch }: Ctx, { profileId, isGameMaster }: Ctx): Promise<void> {
        const player = await dispatch('findPlayerFromProfileId', profileId);
        const editablePlayer = _.cloneDeep(player);
        editablePlayer.isGameMaster = isGameMaster;
        const patch = jsonPatch.compare(player, editablePlayer);
        if (patch.length > 0) {
            await dispatch('update', { id: player._id, patch });
        }
    },
    async changePlayerMap({ dispatch }: Ctx, { mapId, player }: Ctx): Promise<void> {
        await dispatch('setCurrentMapForPlayer', { mapId, player });
        const argumentFiller = {};
        dispatch('drawing/clearLines', argumentFiller, { root: true });
    },
    async setCurrentMapForPlayer({ dispatch }: Ctx, { mapId, player }: Ctx): Promise<void> {
        const modifiedPlayer = _.cloneDeep(player);
        modifiedPlayer.mapId = mapId;
        const patch = jsonPatch.compare(player, modifiedPlayer);
        if (patch.length > 0) {
            await dispatch('update', { id: player._id, patch });
        }
    },
    findPlayerFromProfileId({ getters }: Ctx, profileId: string): Ctx {
        const players = getters.getPlayers;
        return _.find(players, (player: Ctx) => player.userId === profileId);
    },
};

function createGmGroup(players: Ctx): Record<string, string> {
    return _.reduce(
        players,
        (gameMasters: Record<string, string>, player: Ctx) => {
            if (player.isGameMaster) {
                gameMasters[player.userId] = player.userId;
            }
            return gameMasters;
        },
        {},
    );
}

function createNonGmGroup(players: Ctx, assumedGameMasters: Record<string, string>): Record<string, string> {
    return _.reduce(
        players,
        (nonGameMasters: Record<string, string>, player: Ctx) => {
            if (!player.isGameMaster && !assumedGameMasters[player.userId]) {
                nonGameMasters[player.userId] = player.userId;
            }
            return nonGameMasters;
        },
        {},
    );
}

const PlayerStore = {
    namespaced: true,
    getters,
    actions,
};

export default PlayerStore;
